import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Wizard from '@ublique/components/Wizard'
import CronScheduledStage from 'components/CronScheduledStage'
import {
  CRONTAB_STAGE,
  HEADING_LABEL_RULES,
  HEADING_LABEL_CRONTAB,
  RULES_STAGE
} from '../constants'
import RulesGenerator from 'components/RulesGenerator'
import Footer from '../components/Footer'
import { buildRulesConfig } from '../utils/rules'
import { isFunction, keys } from 'lodash'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import Loading from '@ublique/components/Loading'
import TextInput from '@ublique/components/TextInput'
import { processedPurgerData } from '../helpers'

function PurgerModal({ data, open, close }) {
  const { onSave, defaultPurgerData, isPurgerEditing, jobTypes, t } = data

  const { t: tScenarioManager } = useTranslation(['Scenario', 'Wizard'])
  const { t: tPurger } = useTranslation('PurgerPlatform')

  const [stage, setStage] = useState(CRONTAB_STAGE)
  const [headingLabel, setHeadingLabel] = useState(HEADING_LABEL_CRONTAB)
  const [crontabData, setCrontabData] = useState()
  const [isLoading, setIsLoading] = useState()
  const [rule, setRule] = useState(defaultPurgerData.query ?? '')
  const [scenarioPurgerName, setScenarioPurgerName] = useState(
    defaultPurgerData.scenarioPurgeName ?? ''
  )
  const [invalidMessage, setInvalidMessage] = useState()

  const { scenarioModelConfig } = useStoreSelector('appConfig')
  const { email } = useStoreSelector('user')
  const { tableActions, additionalColumns = [] } = useStoreSelector('scenario')
  const instances = useStoreSelector('instances')

  const scenarioTypes = useMemo(
    () => keys(scenarioModelConfig?.wizard?.config ?? {}),
    [scenarioModelConfig]
  )
  const scenarioInstances = useMemo(
    () =>
      (instances ?? [])
        .filter((scenario) => !scenario.config)
        .map((obj) => obj.name ?? obj.title ?? ''),
    [instances]
  )

  const scenarioStatuses = useMemo(
    () => keys(tableActions ?? {}),
    [tableActions]
  )

  const changeOpen = (value) => {
    if (!value) {
      setStage(CRONTAB_STAGE)
      setHeadingLabel(HEADING_LABEL_CRONTAB)
    }
    close()
  }

  const saveCrontabData = async (data) => {
    setCrontabData(data)
    if (isPurgerEditing) {
      onFormSave(data)
      close()
    } else {
      setStage(RULES_STAGE)
      setHeadingLabel(HEADING_LABEL_RULES)
    }
  }

  const rulesConfig = useMemo(
    buildRulesConfig({
      t: tScenarioManager,
      additionalColumns,
      scenarioTypes,
      scenarioInstances,
      scenarioStatuses
    }),
    [
      tScenarioManager,
      additionalColumns,
      scenarioTypes,
      scenarioInstances,
      scenarioStatuses
    ]
  )

  const onFormSave = async (data) => {
    if (!isFunction(onSave)) {
      close()
      return
    }

    if (!scenarioPurgerName) {
      setInvalidMessage(tPurger('PROVIDE_SCENARIO_NAME'))
      return
    }
    setIsLoading(true)

    const body = processedPurgerData({
      purgerData: isPurgerEditing ? data : crontabData,
      query: rule,
      scenarioPurgerName,
      email
    })

    try {
      await onSave(body)
      setIsLoading(false)
      close()
    } catch (error) {
      console.error(error)
    }
  }

  const onChange = (e) => {
    setScenarioPurgerName(e.target.value)
    if (e.target.value) {
      setInvalidMessage()
    } else {
      setInvalidMessage(tPurger('PROVIDE_SCENARIO_NAME'))
    }
  }

  return (
    <div id='wizard-container' className='creation-step'>
      <Wizard
        open={open}
        onClose={close}
        onRequestSubmit={onSave}
        headingLabel={headingLabel}
      >
        {isLoading && <Loading />}
        {stage === CRONTAB_STAGE && (
          <CronScheduledStage
            changeOpen={changeOpen}
            saveScheduledFormData={saveCrontabData}
            defaultScheduledData={defaultPurgerData}
            isScheduledEditing={isPurgerEditing}
            jobTypes={jobTypes}
          />
        )}
        {stage === RULES_STAGE && (
          <>
            <div className='scenario-purger-name-input'>
              <TextInput
                onChange={onChange}
                labelText={tPurger('ScenarioName')}
                invalid={invalidMessage}
                invalidText={invalidMessage}
                value={scenarioPurgerName}
              />
            </div>
            <RulesGenerator
              t={t}
              ns='PurgerStep'
              changeOpen={changeOpen}
              config={rulesConfig}
              saveRule={setRule}
            />
            <Footer
              t={t}
              onSubmit={onFormSave}
              disabled={!rule}
              onCancelClick={() => changeOpen(false)}
            />
          </>
        )}
      </Wizard>
    </div>
  )
}

export default PurgerModal
